import { addUnits, startOfUnit } from './timeScale'
import type { TimelineEvent } from './types'

export type BandState = 'free' | 'low' | 'none' | 'outside' | 'closed'
export type SummaryState = 'free' | 'partial' | 'none' | 'closed'
export type StartSpec = number[] | { from: number, to: number, step: number }

export interface TickPattern {
  left: number
  width: number
  spacing: number
}

export interface AvailableTimes {
  starttimes?: string[]
  endtimes?: Record<string, string[]>
}

const MINUTE = 60000

export function isBand (event: TimelineEvent): boolean {
  return !!(event.data && event.data.kind === 'band')
}

export function isDaySummary (event: TimelineEvent): boolean {
  return !!(event.data && event.data.kind === 'daysummary')
}

export function isBookable (event: TimelineEvent): boolean {
  return isBand(event) && (event.data.state === 'free' || event.data.state === 'low')
}

// Minutes are added through the calendar so that times stay on the wall clock across DST changes
export function atMinute (day: Date | number, minute: number): number {
  const d = startOfUnit(new Date(day), 'day')
  return new Date(d.getFullYear(), d.getMonth(), d.getDate(), 0, minute).getTime()
}

export function minuteOfDay (time: number): number {
  const d = new Date(time)
  return d.getHours() * 60 + d.getMinutes()
}

export function startMinutes (spec: StartSpec | null | undefined): number[] {
  if (!spec) {
    return []
  }
  if (Array.isArray(spec)) {
    return spec.map(Number)
  }
  const list: number[] = []
  const step = Math.max(1, Number(spec.step))
  for (let m = Number(spec.from); m <= Number(spec.to); m += step) {
    list.push(m)
  }
  return list
}

export function bandStarts (event: TimelineEvent): number[] {
  if (!event.data || !event.data.starts) {
    return []
  }
  return startMinutes(event.data.starts).map(m => atMinute(event.start, m))
}

// The slot under the pointer: the last start at or before it, while the pointer is still before that slot's end.
// Loaded lengths give the end (the shortest one). Without them, the last start at or before the pointer is used.
// A pointer before every start, or past a slot whose end is known, gets the first start after it.
export function startForTap (event: TimelineEvent, time: number, endsFor?: (start: number) => number[]): number | null {
  if (!isBookable(event)) {
    return null
  }
  const starts = bandStarts(event)
  if (!starts.length) {
    return null
  }
  let previous: number | null = null
  for (const start of starts) {
    if (start > time) {
      break
    }
    previous = start
  }
  if (previous !== null && slotContains(previous, time, endsFor)) {
    return previous
  }
  for (const start of starts) {
    if (start >= time) {
      return start
    }
  }
  return null
}

function slotContains (start: number, time: number, endsFor?: (start: number) => number[]): boolean {
  if (!endsFor) {
    return true
  }
  const ends = endsFor(start)
  if (!ends.length) {
    return true
  }
  let slotEnd = ends[0]
  for (let i = 1; i < ends.length; i++) {
    if (ends[i] < slotEnd) {
      slotEnd = ends[i]
    }
  }
  return time < slotEnd
}

export function nearestStart (events: TimelineEvent[], time: number): number | null {
  let best: number | null = null
  for (const event of events) {
    if (!isBookable(event)) {
      continue
    }
    for (const s of bandStarts(event)) {
      if (best === null || Math.abs(s - time) < Math.abs(best - time)) {
        best = s
      }
    }
  }
  return best
}

// Ticks are drawn as one repeating gradient for even starts, or not at all when they would be too dense to read
export function tickPattern (event: TimelineEvent, pxPerMinute: number, offsetPx = 0, minSpacing = 6): TickPattern | number[] | null {
  const spec = event.data && event.data.starts as StartSpec | undefined
  if (!spec) {
    return null
  }
  const base = minuteOfDay(event.start.getTime())
  if (!Array.isArray(spec)) {
    const spacing = Number(spec.step) * pxPerMinute
    if (spacing < minSpacing) {
      return null
    }
    return {
      left: (Number(spec.from) - base) * pxPerMinute - offsetPx,
      width: (Number(spec.to) - Number(spec.from)) * pxPerMinute + 1,
      spacing
    }
  }
  const marks = spec.map(m => (Number(m) - base) * pxPerMinute - offsetPx)
  for (let i = 1; i < marks.length; i++) {
    if (marks[i] - marks[i - 1] < minSpacing) {
      return null
    }
  }
  return marks
}

export function timeKey (time: number): string {
  const d = new Date(time)
  return d.getHours() + '.' + String(d.getMinutes()).padStart(2, '0')
}

export function dateKey (time: number): string {
  const d = new Date(time)
  return d.getFullYear() + '-' + String(d.getMonth() + 1).padStart(2, '0') + '-' + String(d.getDate()).padStart(2, '0')
}

// End times are "H.mm", "24.00" for midnight or "H.mm+N" when they fall N days later
export function parseTimeOfDay (day: Date | number, value: string): number {
  const [time, extra] = String(value).split('+')
  const [h, m] = time.replace(':', '.').split('.')
  return atMinute(addUnits(startOfUnit(new Date(day), 'day'), 'day', Number(extra) || 0), Number(h) * 60 + (Number(m) || 0))
}

export function endTimesFor (times: AvailableTimes | null | undefined, start: number): number[] {
  if (!times || !times.endtimes) {
    return []
  }
  const list = times.endtimes[timeKey(start)] || []
  return list.map(value => parseTimeOfDay(start, value)).filter(end => end > start).sort((a, b) => a - b)
}

export function nearestEnd (ends: number[], time: number): number | null {
  let best: number | null = null
  for (const end of ends) {
    if (best === null || Math.abs(end - time) < Math.abs(best - time)) {
      best = end
    }
  }
  return best
}

export function bandAt (events: TimelineEvent[], time: number): TimelineEvent | null {
  for (const event of events) {
    if (isBand(event) && event.start.getTime() <= time && event.end.getTime() > time) {
      return event
    }
  }
  return null
}

const BUCKET_NONE = '#e5484d'
const BUCKET_LOW = '#f0b429'
const BUCKET_FREE = '#2f7d32'

export function bucketGradient (buckets: string | null | undefined): string | null {
  if (!buckets) {
    return null
  }
  const step = 100 / buckets.length
  const stops: string[] = []
  for (let i = 0; i < buckets.length; i++) {
    const digit = Number(buckets[i])
    const color = digit === 0 ? BUCKET_NONE : digit < 5 ? BUCKET_LOW : BUCKET_FREE
    stops.push(color + ' ' + (i * step).toFixed(2) + '% ' + ((i + 1) * step).toFixed(2) + '%')
  }
  return 'linear-gradient(to right, ' + stops.join(', ') + ')'
}

export function formatDuration (minutes: number, hourLabel = 'h', minuteLabel = 'min'): string {
  const h = Math.floor(minutes / 60)
  const m = Math.round(minutes % 60)
  if (!h) {
    return m + ' ' + minuteLabel
  }
  return m ? h + ' ' + hourLabel + ' ' + m + ' ' + minuteLabel : h + ' ' + hourLabel
}

function reservedLevel (event: TimelineEvent): number {
  const match = /reserved_(\d+)/.exec(event.className || '')
  return match ? Number(match[1]) : 0
}

interface OccupancyBucket {
  rowKey: string
  day: number
  count: number
  participants: number
  minutes: number
  first: number
  last: number
  peak: TimelineEvent | null
}

/**
 * Combines same-day reservations and pool overview blocks of the given rows into one block per row and day
 *
 * Reservations that span several days stay as they are. A reservation on several rows keeps its other rows.
 */
export function aggregateOccupancy (events: TimelineEvent[], shouldAggregate: (rowKey: string, day: number) => boolean): TimelineEvent[] {
  const result: TimelineEvent[] = []
  const buckets = new Map<string, OccupancyBucket>()
  for (const event of events) {
    const start = event.start.getTime()
    const end = event.end.getTime()
    const day = startOfUnit(event.start, 'day').getTime()
    const keys = event.rowKeys.filter(key => shouldAggregate(key, day))
    if (!keys.length || startOfUnit(new Date(end - 1), 'day').getTime() !== day) {
      result.push(event)
      continue
    }
    if (keys.length < event.rowKeys.length) {
      result.push(Object.assign({}, event, { rowKeys: event.rowKeys.filter(key => !keys.includes(key)) }))
    }
    const isOverview = /^overview-/.test(event.id)
    for (const rowKey of keys) {
      const id = rowKey + '|' + day
      let bucket = buckets.get(id)
      if (!bucket) {
        bucket = { rowKey, day, count: 0, participants: 0, minutes: 0, first: start, last: end, peak: null }
        buckets.set(id, bucket)
      }
      bucket.first = Math.min(bucket.first, start)
      bucket.last = Math.max(bucket.last, end)
      if (isOverview) {
        if (!bucket.peak || reservedLevel(event) > reservedLevel(bucket.peak)) {
          bucket.peak = event
        }
      } else {
        bucket.count++
        bucket.participants += parseInt(event.data && event.data.participants) || 0
        bucket.minutes += (end - start) / MINUTE
      }
    }
  }
  for (const bucket of buckets.values()) {
    const peak = bucket.peak
    result.push({
      id: 'occupancy-' + bucket.rowKey + '-' + bucket.day,
      start: new Date(bucket.day),
      end: addUnits(new Date(bucket.day), 'day', 1),
      rowKeys: [bucket.rowKey],
      draggable: false,
      resizable: false,
      className: peak ? peak.className : undefined,
      data: {
        kind: peak ? 'overviewpeak' : 'occupancy',
        text: peak && peak.data ? peak.data.text : '',
        count: bucket.count,
        participants: bucket.participants,
        minutes: bucket.minutes,
        first: bucket.first,
        last: bucket.last
      }
    })
  }
  return result
}
